import { useRef, type KeyboardEvent } from 'react';
import { cn } from '@ai-tutor/ui';
import type { FocusView } from '@/lib/focus/focusMachine';
import { sakuraPoster } from './sakura/sakuraStage';

const VIEWS: { id: FocusView; name: string; desc: string }[] = [
  {
    id: 'classic',
    name: 'Classic',
    desc: 'The timer, a quiet ring and a row of round stops.',
  },
  {
    id: 'sakura',
    name: 'Sakura',
    desc: 'A bare cherry tree by a lake. Blossom comes out with every round you focus; in the last round, its petals fall.',
  },
];

interface ViewPickerProps {
  value: FocusView;
  onChange: (view: FocusView) => void;
  /** Flow has no plan to draw, so only Classic is offered. */
  flow: boolean;
  night: boolean;
}

/**
 * "How do you want to see it?" - picked at set-up for this session only, and
 * fixed once it starts. A real radiogroup: arrows move between the choices.
 */
export function ViewPicker({ value, onChange, flow, night }: ViewPickerProps) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKey = (e: KeyboardEvent, i: number) => {
    if (
      !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key) ||
      flow
    )
      return;
    e.preventDefault();
    const next =
      (i +
        (e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1) +
        VIEWS.length) %
      VIEWS.length;
    onChange(VIEWS[next].id);
    refs.current[next]?.focus();
  };
  const current = VIEWS.find(v => v.id === value) ?? VIEWS[0];
  return (
    <div className='space-y-2'>
      <p
        id='focus-view-label'
        className='text-sm font-semibold text-foreground'
      >
        How do you want to see it?
      </p>
      <div
        role='radiogroup'
        aria-labelledby='focus-view-label'
        className='flex gap-2'
      >
        {VIEWS.map((v, i) => {
          const off = flow && v.id !== 'classic';
          const on = value === v.id;
          return (
            <button
              key={v.id}
              ref={el => {
                refs.current[i] = el;
              }}
              type='button'
              role='radio'
              aria-checked={on}
              disabled={off}
              tabIndex={on ? 0 : -1}
              title={
                off
                  ? 'Views need a planned session. Switch to Rounds.'
                  : undefined
              }
              onClick={() => onChange(v.id)}
              onKeyDown={e => onKey(e, i)}
              className={cn(
                'flex w-24 flex-col items-center gap-1.5 rounded-xl border bg-card p-2 text-xs font-medium text-foreground transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40',
                on
                  ? 'border-ring shadow-[inset_0_0_0_1px_hsl(var(--ring))]'
                  : 'border-border hover:border-input'
              )}
            >
              <span
                className='flex h-9 w-full items-center justify-center overflow-hidden rounded-lg bg-secondary bg-cover bg-center'
                style={
                  v.id === 'sakura'
                    ? { backgroundImage: `url(${sakuraPoster(night)})` }
                    : undefined
                }
                aria-hidden='true'
              >
                {v.id === 'classic' && (
                  <span className='h-5 w-5 rounded-full border-2 border-brand-500 dark:border-brand-300' />
                )}
              </span>
              {v.name}
            </button>
          );
        })}
      </div>
      <p className='text-xs text-muted-foreground'>
        {flow
          ? 'Flow has no plan to draw, so it uses the classic view.'
          : current.desc}{' '}
        Chosen for this session only.
      </p>
    </div>
  );
}
